import React from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../hooks/useScollToTop";

import ActorGroupSelectionModal from "./ActorGroupSelectionModal";
import ActorSelectionModal from "./ActorSelectionModal";
import ActorUnregistrationModal from "./ActorUnregistrationModal";

import ConvocationsAdminHeader from "./components/ConvocationsAdminHeader";
import ConvocationsDesktopTable from "./components/ConvocationsDesktopTable";
import ConvocationsFiltersBar from "./components/ConvocationsFiltersBar";
import ConvocationsMobileList from "./components/ConvocationsMobileList";
import ConvocationsPagination from "./components/ConvocationsPagination";
import ConvocationsResultsSummary from "./components/ConvocationsResultsSummary";

import useConvocationModals from "./hooks/useConvocationModals";
import useConvocationsData from "./hooks/useConvocationsData";
import useConvocationsFilters from "./hooks/useConvocationsFilters";

export default function ConvocationsAdminPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const { events, loading, refreshEvents } = useConvocationsData();

  const {
    searchText,
    setSearchText,
    debouncedSearch,

    selectedType,
    setSelectedType,

    selectedOrganizer,
    setSelectedOrganizer,
    organizerOptions,

    selectedLevel,
    setSelectedLevel,
    levelOptions,

    selectedDateFilter,
    setSelectedDateFilter,

    onlyWithConvocations,
    setOnlyWithConvocations,

    typeOptions,
    filteredEvents,
    pageItems,

    page,
    setPage,
    pageCount,

    stats,
    resetFilters,
  } = useConvocationsFilters(events);

  const {
    selectedEventId,
    currentEvent,

    isActorSelectionOpen,
    isGroupSelectionOpen,
    isUnregistrationOpen,

    openActorSelection,
    openGroupSelection,
    openUnregistration,
    closeModal,
  } = useConvocationModals(events);

  const goToEventPage = (eventId) => {
    navigate(`/appli/convocations-admin/${eventId}`);
  };

  const sharedListProps = {
    rows: pageItems,
    loading,
    onRegister: openActorSelection,
    onRegisterByGroup: openGroupSelection,
    onUnregister: openUnregistration,
    onView: goToEventPage,
  };

  return (
    <>
      <main className="min-h-screen w-full bg-slate-50/70 pb-10 pt-24 font-satoshi dark:bg-slate-950 sm:pt-28">
        <div className="mx-auto w-full max-w-[1500px] px-3 sm:px-5 lg:px-6">
          <ConvocationsAdminHeader stats={stats} loading={loading} />

          <div className="mt-5">
            <ConvocationsFiltersBar
              searchText={searchText}
              onSearchChange={setSearchText}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              typeOptions={typeOptions}
              selectedOrganizer={selectedOrganizer}
              onOrganizerChange={setSelectedOrganizer}
              organizerOptions={organizerOptions}
              selectedLevel={selectedLevel}
              onLevelChange={setSelectedLevel}
              levelOptions={levelOptions}
              selectedDateFilter={selectedDateFilter}
              onDateFilterChange={setSelectedDateFilter}
              onlyWithConvocations={onlyWithConvocations}
              onOnlyWithConvocationsChange={setOnlyWithConvocations}
              onReset={resetFilters}
              loading={loading}
            />
          </div>

          <div className="mt-4">
            <ConvocationsResultsSummary
              resultCount={filteredEvents.length}
              displayedCount={pageItems.length}
              search={debouncedSearch}
              selectedType={selectedType}
              selectedDateFilter={selectedDateFilter}
              onlyWithConvocations={onlyWithConvocations}
              page={page}
              pageCount={pageCount}
            />
          </div>

          <div className="mt-5">
            <ConvocationsDesktopTable {...sharedListProps} />
            <ConvocationsMobileList {...sharedListProps} />
          </div>

          {!loading && (
            <ConvocationsPagination
              page={page}
              pageCount={pageCount}
              onPageChange={setPage}
            />
          )}
        </div>
      </main>

      <ActorSelectionModal
        isOpen={isActorSelectionOpen}
        onClose={closeModal}
        eventId={selectedEventId}
        eventTitle={currentEvent?.titre}
        eventStart={currentEvent?.start}
        refreshList={refreshEvents}
      />

      <ActorGroupSelectionModal
        isOpen={isGroupSelectionOpen}
        onClose={closeModal}
        eventId={selectedEventId}
        eventTitle={currentEvent?.titre}
        eventStart={currentEvent?.start}
        refreshList={refreshEvents}
      />

      <ActorUnregistrationModal
        isOpen={isUnregistrationOpen}
        onClose={closeModal}
        eventId={selectedEventId}
        eventTitle={currentEvent?.titre}
        eventStart={currentEvent?.start}
        refreshList={refreshEvents}
      />

      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        theme="colored"
      />
    </>
  );
}
